import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check for admin routes
  if (config.url?.startsWith('/admin')) {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
  } else {
    const token = localStorage.getItem('subguard_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for admin routes
      if (!error.config?.url?.startsWith('/admin')) {
        localStorage.removeItem('subguard_token');
        localStorage.removeItem('subguard_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  sendOtp: (email) => api.post('/auth/send-otp', { email }),
  verifyOtp: (email, otp, rememberMe = false, isSignup = false) => api.post('/auth/verify-otp', { email, otp, remember_me: rememberMe, is_signup: isSignup }),
  getMe: () => api.get('/auth/me'),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
  logout: () => api.post('/auth/logout'),
  updateProfile: (data) => api.patch('/auth/profile', data),
  deleteAccount: () => api.delete('/auth/account'),
};

// Admin API
export const adminApi = {
  sendOtp: (email) => api.post('/admin/send-otp', { email }),
  verifyOtp: (email, otp) => api.post('/admin/verify-otp', { email, otp }),
  getStats: () => api.get('/admin/stats'),
  getUsers: (skip = 0, limit = 50) => api.get(`/admin/users?skip=${skip}&limit=${limit}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getSubscriptions: (skip = 0, limit = 50) => api.get(`/admin/subscriptions?skip=${skip}&limit=${limit}`),
  getScans: (skip = 0, limit = 50) => api.get(`/admin/scans?skip=${skip}&limit=${limit}`),
  getPayments: (skip = 0, limit = 50) => api.get(`/admin/payments?skip=${skip}&limit=${limit}`),
  getLogs: (skip = 0, limit = 50) => api.get(`/admin/logs?skip=${skip}&limit=${limit}`),
  // Stripe settings
  getStripeSettings: () => api.get('/admin/stripe-settings'),
  updateStripeSettings: (data) => api.put('/admin/stripe-settings', data),
  toggleStripeMode: () => api.post('/admin/stripe-settings/toggle-mode'),
  // App settings
  getAppSettings: () => api.get('/admin/app-settings'),
  updateAppSettings: (data) => api.put('/admin/app-settings', data),
  toggleOtpDisplay: () => api.post('/admin/app-settings/toggle-otp-display'),
  // Waitlist
  clearWaitlist: () => api.delete('/admin/waitlist/clear'),
};

// Subscriptions API
export const subscriptionsApi = {
  getAll: () => api.get('/subscriptions'),
  getOne: (id) => api.get(`/subscriptions/${id}`),
  create: (data) => api.post('/subscriptions', data),
  update: (id, data) => api.patch(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  startCancelWorkflow: (id, reason) => api.post(`/subscriptions/${id}/cancel-workflow`, { subscription_id: id, reason }),
  markCancelled: (id) => api.post(`/subscriptions/${id}/mark-cancelled`),
  generateNegotiation: (id, currentPrice, desiredPrice, reason) => 
    api.post(`/subscriptions/${id}/negotiate`, {
      subscription_id: id,
      current_price: currentPrice,
      desired_price: desiredPrice,
      reason,
    }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getSavingsHistory: () => api.get('/dashboard/savings-history'),
};

// Payments API
export const paymentsApi = {
  createCheckout: (plan, originUrl) => api.post('/payments/create-checkout', { plan, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getQuickCheckStatus: () => api.get('/payments/quick-check-status'),
};

// Email API
export const emailApi = {
  // Gmail
  startGmailOAuth: (redirectUri) => api.post('/email/gmail/start', { redirect_uri: redirectUri }),
  scanGmail: (connectionId) => api.post('/email/gmail/scan', { connection_id: connectionId }),
  disconnectGmail: (email, connectionId) => api.delete('/email/gmail/disconnect', { 
    params: { email, connection_id: connectionId } 
  }),
  // Outlook
  startOutlookOAuth: (redirectUri) => api.post('/email/outlook/start', { redirect_uri: redirectUri }),
  scanOutlook: (connectionId) => api.post('/email/outlook/scan', { connection_id: connectionId }),
  disconnectOutlook: (email, connectionId) => api.delete('/email/outlook/disconnect', { 
    params: { email, connection_id: connectionId } 
  }),
  // Common
  getConnections: () => api.get('/email/connections'),
  saveScannedSubscriptions: (subscriptions) => api.post('/email/save-scanned', { subscriptions }),
};

// Bank Statement Scanning API
export const scanApi = {
  scanBankStatement: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/scan/bank-statement', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minute timeout for large files
    });
  },
};

// Referral & Badges API
export const referralApi = {
  getReferralCode: () => api.get('/referral/code'),
  applyReferralCode: (code) => api.post('/referral/apply', null, { params: { referral_code: code } }),
  getStats: () => api.get('/referral/stats'),
  getBadges: () => api.get('/badges'),
};

// Cancel Help API (Public - no auth required)
export const cancelHelpApi = {
  sendOtp: (email) => api.post('/cancel-help/send-otp', { email }),
  verifyOtp: (email, otp) => api.post('/cancel-help/verify-otp', { email, otp }),
  getSession: (sessionId) => api.get(`/cancel-help/session/${sessionId}`),
  chat: (sessionId, message, serviceName) => api.post('/cancel-help/chat', { 
    session_id: sessionId, 
    message, 
    service_name: serviceName 
  }),
  getPopularServices: () => api.get('/cancel-help/popular-services'),
  quickSignup: (sessionId) => api.post('/cancel-help/quick-signup', { session_id: sessionId }),
};

// Demo API (Public)
export const demoApi = {
  setup: () => api.post('/demo/setup'),
  getInfo: () => api.get('/demo/info'),
};

// Feature Requests API
export const featureRequestsApi = {
  create: (title, description, category = 'general') => 
    api.post('/feature-requests', { title, description, category }),
  getAll: () => api.get('/feature-requests'),
  vote: (requestId) => api.post(`/feature-requests/${requestId}/vote`),
  // Admin
  adminGetAll: () => api.get('/admin/feature-requests'),
  adminUpdate: (requestId, status, adminNotes) => 
    api.patch(`/admin/feature-requests/${requestId}`, null, { params: { status, admin_notes: adminNotes } }),
  adminDelete: (requestId) => api.delete(`/admin/feature-requests/${requestId}`),
};

export default api;
